/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useDispatch, useSelector } from "react-redux"
import { useLazyFetchMenuQuery, useSortDepartmentsMutation } from "queries"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { fetchSortDepartments } from "utils/rtk_migration_helper"
import { sortMenuItemNewLogic } from "utils/sort_helper"
import { AppState } from "store/store.type"
import { Department } from "types/models/menu"

interface ISubmitDepartmentSortOptions {
  /**
   * Parent department ID — set when sorting sub-departments within a
   * department, omitted when sorting top-level departments.
   */
  parentDepartmentID?: string
  successMessage: string
}

interface IUseSubmitDepartmentSortReturn {
  submitDepartmentSort: (
    sortedDepartments: Department[],
    newIndex: number,
    options: ISubmitDepartmentSortOptions,
  ) => void
  isRtkFlagLoading: boolean
}

/**
 * Shared sort submission for the department and sub-department overview
 * lists: computes the UCS-aware sort payload and sends it through the
 * RTK migration helper. Callers keep their own rearrange tracking event
 * and debounce wrapper.
 */
export const useSubmitDepartmentSort = (): IUseSubmitDepartmentSortReturn => {
  const dispatch = useDispatch()
  const [sortDepartmentsRtk] = useSortDepartmentsMutation()
  const {
    flags: {
      sortDepartments: isRtkSortDepartmentsEnabled,
      fetchMenu: isRtkFetchMenuEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [fetchMenuRtk] = useLazyFetchMenuQuery()

  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    isMartDepartmentStructure,
    isSellingTimeStructure,
    enablePartialMenu,
    isUCS,
  } = useSelector((state: AppState) => state?.menu)

  const submitDepartmentSort = (
    sortedDepartments: Department[],
    newIndex: number,
    { parentDepartmentID, successMessage }: ISubmitDepartmentSortOptions,
  ) => {
    const sorts = isUCS
      ? sortMenuItemNewLogic({
          newIndex,
          newList: sortedDepartments,
          sortKey: "sortOrder",
          itemKey: "itemClassID",
        })
      : sortedDepartments.map((department, index) => ({
          resourceID: department.itemClassID ?? "",
          sortOrder: sortedDepartments.length - index,
        }))

    // While migration flags are loading, do not fall back to old Redux: the enabled bit is
    // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
    // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
    // the trigger UI while loading so clicks are not silently dropped.
    if (!isRtkFlagLoading) {
      fetchSortDepartments({
        dispatch,
        merchantID,
        merchantGroupID,
        menuGroupID,
        parentDepartmentID,
        sorts,
        successMessage,
        isMartDepartmentStructure,
        isSellingTimeStructure,
        enablePartialMenu,
        isRtkSortDepartmentsEnabled,
        sortDepartmentsRtk,
        isRtkFetchMenuEnabled,
        fetchMenuRtk,
      })
    }
  }

  return { submitDepartmentSort, isRtkFlagLoading }
}
